import { createEventSource, EventSourceClient, FetchLike } from 'eventsource-client'

import { HttpStatus } from '@marketplace-web/core-api/api-client-util'
import {
  logSSEError,
  logSSEMessage,
  SSEEventData,
  SSEConnectionOptions,
} from '@marketplace-web/messaging/sse-connection-data'
import { refreshSessionTokens } from '@marketplace-web/session-management/session-management-interceptor-util'

export type EventHandler = (data: SSEEventData) => void

class SSEConnectionManager {
  private static instance: SSEConnectionManager

  private eventSource: EventSourceClient | null = null

  private connectionOptions: SSEConnectionOptions | null = null

  private eventHandlers: Map<string, Set<EventHandler>> = new Map()

  private reconnectTimeoutId: NodeJS.Timeout | null = null

  private heartbeatTimeout: number = 12000

  private heartbeatTimer: NodeJS.Timeout | null = null

  private static createCustomFetch(): FetchLike {
    return async (url, init) => {
      const response = await fetch(url, init)

      if (response.status === HttpStatus.Unauthorized) {
        logSSEMessage('SSE subscribe received 401 Unauthorized, refreshing session token...')
        try {
          // TODO: this is a temporary solution, as it might conflict with tokenRefreshInterceptor
          await refreshSessionTokens()
          logSSEMessage('Session token refreshed successfully')
        } catch (error) {
          logSSEError(error, 'Error refreshing token for SSE connection')
        }
      }

      return response
    }
  }

  public static getInstance(): SSEConnectionManager {
    if (!SSEConnectionManager.instance) {
      SSEConnectionManager.instance = new SSEConnectionManager()
    }

    return SSEConnectionManager.instance
  }

  public enable(options: SSEConnectionOptions): void {
    this.connectionOptions = options
    this.connect(options)
  }

  public disable(): void {
    this.connectionOptions = null
    this.disconnect()
  }

  private connect(options: SSEConnectionOptions): void {
    try {
      const { url, headers = {} } = options

      this.eventSource = createEventSource({
        fetch: SSEConnectionManager.createCustomFetch(),
        url,
        headers,
        onComment: () => {
          this.startHeartbeatTimer()
        },
        onMessage: (event: any) => {
          try {
            const parsedData = JSON.parse(event.data)
            logSSEMessage('Message received:', parsedData)
            this.notifyHandlers('message', parsedData)
          } catch (error) {
            logSSEError(error, 'Failed to parse message data')
          }
        },
        onConnect: () => {
          this.startHeartbeatTimer()
        },
        onScheduleReconnect: (info: { delay: number }) => {
          this.clearHeartbeatTimer()
          logSSEMessage(`Scheduling reconnect in ${info.delay}ms`)
        },
      })

      logSSEMessage('Connected to:', url)
    } catch (error) {
      logSSEError(error, 'Failed to create EventSource')
    }
  }

  private startHeartbeatTimer(): void {
    this.clearHeartbeatTimer()
    this.heartbeatTimer = setTimeout(() => {
      logSSEMessage(
        `Heartbeat timeout - no keep-alive for ${this.heartbeatTimeout}s, reconnecting...`,
      )
      this.disconnect()
      this.scheduleReconnect()
    }, this.heartbeatTimeout)
  }

  private clearHeartbeatTimer(): void {
    if (!this.heartbeatTimer) return

    clearTimeout(this.heartbeatTimer)
    this.heartbeatTimer = null
  }

  private notifyHandlers(eventType: string, data: any): void {
    const handlers = this.eventHandlers.get(eventType)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data)
        } catch (error) {
          logSSEError(error, 'Error in event handler')
        }
      })
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId)
    }
    const delay = 1000
    logSSEMessage(`Scheduling reconnect in ${delay} second`)

    this.reconnectTimeoutId = setTimeout(() => {
      if (this.connectionOptions) {
        this.connect(this.connectionOptions)
      }
    }, delay)
  }

  private disconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    logSSEMessage('Disconnected')
  }

  public subscribe(eventType: string, handler: EventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set())
    }
    this.eventHandlers.get(eventType)!.add(handler)
  }

  public unsubscribe(eventType: string, handler: EventHandler): void {
    try {
      const handlers = this.eventHandlers.get(eventType)
      if (!handlers) {
        logSSEMessage(`No handlers found for event type: ${eventType}`)

        return
      }

      const wasRemoved = handlers.delete(handler)
      if (!wasRemoved) {
        logSSEMessage(`Handler not found for event type: ${eventType}`)
      }

      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType)
        logSSEMessage(`Unsubscribed from ${eventType} (no more handlers)`)
      } else {
        logSSEMessage(`Unsubscribed from ${eventType} (${handlers.size} handlers remaining)`)
      }
    } catch (error) {
      logSSEError(error, `Error during unsubscribe for event type: ${eventType}`)
    }
  }
}

export default SSEConnectionManager
